import { pb } from './Pocketbase'

export interface IPendingSubmission {
  id?: string
  name: string
  brand_text?: string
  brand?: string // relation ID
  country_text?: string
  country?: string // relation ID
  perfumers_text?: string[] // JSON array
  perfumers?: string[] // relation IDs
  top_notes_text?: string[] // JSON array
  top_notes?: string[] // relation IDs
  middle_notes_text?: string[] // JSON array
  middle_notes?: string[] // relation IDs
  bottom_notes_text?: string[] // JSON array
  bottom_notes?: string[] // relation IDs
  description?: string
  year?: number
  price?: string // relation ID
  image?: File
  approved_ref?: string // relation ID to approved_submissions (for edits)
}

export const createPendingSubmission = async (
  submission: IPendingSubmission
): Promise<IPendingSubmission> => {
  const formData = new FormData()

  // Add all text fields
  formData.append('name', submission.name)

  // Optional text fields
  if (submission.brand_text) formData.append('brand_text', submission.brand_text)
  if (submission.country_text) formData.append('country_text', submission.country_text)
  if (submission.description) formData.append('description', submission.description)
  if (submission.year) formData.append('year', submission.year.toString())

  // Relations (single)
  if (submission.brand) formData.append('brand', submission.brand)
  if (submission.country) formData.append('country', submission.country)
  if (submission.price) formData.append('price', submission.price)

  // Relations (multiple) - perfumers
  if (submission.perfumers && submission.perfumers.length > 0) {
    submission.perfumers.forEach((perfumerId) => {
      formData.append('perfumers', perfumerId)
    })
  }

  // Relations (multiple) - notes
  if (submission.top_notes && submission.top_notes.length > 0) {
    submission.top_notes.forEach((noteId) => {
      formData.append('top_notes', noteId)
    })
  }
  if (submission.middle_notes && submission.middle_notes.length > 0) {
    submission.middle_notes.forEach((noteId) => {
      formData.append('middle_notes', noteId)
    })
  }
  if (submission.bottom_notes && submission.bottom_notes.length > 0) {
    submission.bottom_notes.forEach((noteId) => {
      formData.append('bottom_notes', noteId)
    })
  }

  // JSON arrays for custom text perfumers
  if (submission.perfumers_text && submission.perfumers_text.length > 0) {
    formData.append('perfumers_text', JSON.stringify(submission.perfumers_text))
  }

  // JSON arrays for custom text notes
  if (submission.top_notes_text && submission.top_notes_text.length > 0) {
    formData.append('top_notes_text', JSON.stringify(submission.top_notes_text))
  }
  if (submission.middle_notes_text && submission.middle_notes_text.length > 0) {
    formData.append('middle_notes_text', JSON.stringify(submission.middle_notes_text))
  }
  if (submission.bottom_notes_text && submission.bottom_notes_text.length > 0) {
    formData.append('bottom_notes_text', JSON.stringify(submission.bottom_notes_text))
  }

  // File upload
  if (submission.image) {
    formData.append('image', submission.image)
  }

  // Reference to approved submission (for edits)
  if (submission.approved_ref) {
    formData.append('approved_ref', submission.approved_ref)
  }

  const record = await pb.collection('pending_submissions').create<IPendingSubmission>(formData)
  return record
}

export const getPendingSubmissions = async (
  page: number = 1,
  perPage: number = 10
) => {
  const result = await pb.collection('pending_submissions').getList(page, perPage, {
    sort: '-created',
    expand: 'brand,country,perfumers,price,top_notes,middle_notes,bottom_notes,submitted_by',
    filter: 'is_denied = false',
  })
  return result
}

export const searchPendingSubmissions = async (
  page: number = 1,
  perPage: number = 10,
  search: string
) => {
  const filter = pb.filter('is_denied = false && name ~ {:search}', { search })
  const result = await pb.collection('pending_submissions').getList(page, perPage, {
    sort: '-created',
    expand: 'brand,country,perfumers,price,top_notes,middle_notes,bottom_notes,submitted_by',
    filter,
  })
  return result
}

export const getPendingSubmissionById = async (id: string) => {
  const record = await pb.collection('pending_submissions').getOne(id, {
    expand: 'brand,country,perfumers,price,top_notes,middle_notes,bottom_notes,submitted_by',
  })
  return record
}

export const updatePendingSubmission = async (
  id: string,
  submission: Partial<IPendingSubmission>
): Promise<IPendingSubmission> => {
  const formData = new FormData()

  // Add all text fields
  if (submission.name !== undefined) formData.append('name', submission.name)

  // Optional text fields
  if (submission.brand_text !== undefined) {
    formData.append('brand_text', submission.brand_text || null as any)
  }
  if (submission.country_text !== undefined) {
    formData.append('country_text', submission.country_text || null as any)
  }
  if (submission.description !== undefined) {
    formData.append('description', submission.description || null as any)
  }
  if (submission.year !== undefined) {
    formData.append('year', submission.year ? submission.year.toString() : null as any)
  }

  // Relations (single) - use null to clear
  if (submission.brand !== undefined) {
    formData.append('brand', submission.brand || null as any)
  }
  if (submission.country !== undefined) {
    formData.append('country', submission.country || null as any)
  }
  if (submission.price !== undefined) {
    formData.append('price', submission.price || null as any)
  }

  // Relations (multiple) - perfumers
  if (submission.perfumers !== undefined) {
    if (submission.perfumers && submission.perfumers.length > 0) {
      submission.perfumers.forEach((perfumerId) => {
        formData.append('perfumers', perfumerId)
      })
    } else {
      formData.set('perfumers', JSON.stringify([]))
    }
  }

  // Relations (multiple) - notes
  // For relation arrays in Pocketbase, we need to handle them differently
  if (submission.top_notes !== undefined) {
    // Always set the field, even if empty
    if (submission.top_notes && submission.top_notes.length > 0) {
      submission.top_notes.forEach((noteId) => {
        formData.append('top_notes', noteId)
      })
    } else {
      // For empty arrays, append the field with an empty array indicator
      formData.set('top_notes', JSON.stringify([]))
    }
  }
  if (submission.middle_notes !== undefined) {
    if (submission.middle_notes && submission.middle_notes.length > 0) {
      submission.middle_notes.forEach((noteId) => {
        formData.append('middle_notes', noteId)
      })
    } else {
      formData.set('middle_notes', JSON.stringify([]))
    }
  }
  if (submission.bottom_notes !== undefined) {
    if (submission.bottom_notes && submission.bottom_notes.length > 0) {
      submission.bottom_notes.forEach((noteId) => {
        formData.append('bottom_notes', noteId)
      })
    } else {
      formData.set('bottom_notes', JSON.stringify([]))
    }
  }

  // JSON arrays for custom text perfumers
  if (submission.perfumers_text !== undefined) {
    if (submission.perfumers_text && submission.perfumers_text.length > 0) {
      formData.append('perfumers_text', JSON.stringify(submission.perfumers_text))
    } else {
      formData.append('perfumers_text', null as any)
    }
  }

  // JSON arrays for custom text notes
  if (submission.top_notes_text !== undefined) {
    if (submission.top_notes_text && submission.top_notes_text.length > 0) {
      formData.append('top_notes_text', JSON.stringify(submission.top_notes_text))
    } else {
      formData.append('top_notes_text', null as any)
    }
  }
  if (submission.middle_notes_text !== undefined) {
    if (submission.middle_notes_text && submission.middle_notes_text.length > 0) {
      formData.append('middle_notes_text', JSON.stringify(submission.middle_notes_text))
    } else {
      formData.append('middle_notes_text', null as any)
    }
  }
  if (submission.bottom_notes_text !== undefined) {
    if (submission.bottom_notes_text && submission.bottom_notes_text.length > 0) {
      formData.append('bottom_notes_text', JSON.stringify(submission.bottom_notes_text))
    } else {
      formData.append('bottom_notes_text', null as any)
    }
  }

  // File upload
  if (submission.image) {
    formData.append('image', submission.image)
  }

  const record = await pb.collection('pending_submissions').update<IPendingSubmission>(id, formData)
  return record
}

export const deletePendingSubmission = async (id: string): Promise<void> => {
  await pb.collection('pending_submissions').delete(id)
}

/**
 * Get denied submissions with pagination
 */
export const getDeniedSubmissions = async (
  page: number = 1,
  perPage: number = 10
) => {
  const result = await pb.collection('pending_submissions').getList(page, perPage, {
    sort: '-created',
    expand: 'brand,country,perfumers,price,top_notes,middle_notes,bottom_notes,submitted_by',
    filter: 'is_denied = true',
  })
  return result
}

export const searchDeniedSubmissions = async (
  page: number = 1,
  perPage: number = 10,
  search: string
) => {
  const filter = pb.filter('is_denied = true && name ~ {:search}', { search })
  const result = await pb.collection('pending_submissions').getList(page, perPage, {
    sort: '-created',
    expand: 'brand,country,perfumers,price,top_notes,middle_notes,bottom_notes,submitted_by',
    filter,
  })
  return result
}

/**
 * Mark a submission as denied with a reason
 */
export const denySubmission = async (id: string, reason?: string): Promise<void> => {
  await pb.collection('pending_submissions').update(id, {
    is_denied: true,
    denied_reason: reason,
  })
}

/**
 * Restore a denied submission back to pending status
 */
export const restoreDeniedSubmission = async (id: string): Promise<void> => {
  await pb.collection('pending_submissions').update(id, {
    is_denied: false,
    denied_reason: null,
  })
}

/**
 * Creates a pending submission from an approved submission for editing
 * The pending submission will have approved_ref set to the original approved submission ID
 */
export const createPendingFromApproved = async (
  approvedSubmissionId: string
): Promise<IPendingSubmission> => {
  // Fetch the approved submission
  const approved = await pb.collection('approved_submissions').getOne(approvedSubmissionId)

  // Download the image from approved submission
  const imageUrl = pb.files.getURL(approved, approved.image)
  const imageResponse = await fetch(imageUrl)
  const imageBlob = await imageResponse.blob()
  const imageFile = new File([imageBlob], approved.image, { type: imageBlob.type })

  // Create the pending submission with all the data from approved
  const pendingSubmission: IPendingSubmission = {
    name: approved.name,
    brand: approved.brand,
    country: approved.country,
    perfumers: approved.perfumers,
    price: approved.price,
    top_notes: approved.top_notes,
    middle_notes: approved.middle_notes,
    bottom_notes: approved.bottom_notes,
    description: approved.description,
    year: approved.year,
    image: imageFile,
    approved_ref: approvedSubmissionId,
  }

  return await createPendingSubmission(pendingSubmission)
}

/**
 * Check if there is already a pending submission for an approved submission
 * Returns the ID of the pending submission if it exists, or null
 */
export const getPendingByApprovedRef = async (approvedId: string): Promise<string | null> => {
  try {
    const result = await pb.collection('pending_submissions').getFirstListItem(`approved_ref="${approvedId}" && is_denied=false`)
    return result.id
  } catch (error) {
    // Return null if not found
    return null
  }
}
